import React, { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { Loader2 } from 'lucide-react';

const OAuth2Callback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isConnecting = useRef(false);

  useEffect(() => {
    const connectDrive = async () => {
      // Prevent double-execution in React StrictMode
      if (isConnecting.current) return;
      isConnecting.current = true;

      const code = searchParams.get('code');
      const error = searchParams.get('error');

      if (error) {
        console.error('Google OAuth error:', error);
        toast.error(`Authentication failed: ${error}`);
        navigate('/settings?tab=company', { replace: true });
        return;
      }

      if (!code) {
        toast.error('No authorization code found in URL.');
        navigate('/settings?tab=company', { replace: true });
        return;
      }

      try {
        toast.loading('Connecting to your Google Drive...', { id: 'oauth-loading' });
        
        const response = await api.post('/api/settings/google/connect', { code });
        
        toast.dismiss('oauth-loading');
        
        if (response.data.success) {
          toast.success('🎉 Google Drive connected successfully!');
        } else {
          toast.error(response.data.message || 'Failed to connect Google Drive.');
        }
      } catch (err) {
        toast.dismiss('oauth-loading');
        console.error('Failed to exchange auth code:', err);
        toast.error(
          err.response?.data?.message || 'Server error exchanging authorization code.'
        );
      } finally {
        navigate('/settings?tab=company', { replace: true });
      }
    };

    connectDrive();
  }, [searchParams, navigate]);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4">
      <div className="bg-white p-8 rounded-2xl shadow-xl max-w-md w-full text-center space-y-6">
        <div className="flex justify-center">
          <Loader2 className="w-12 h-12 text-blue-600 animate-spin" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-gray-900">Authorizing Google Drive</h2>
          <p className="mt-2 text-sm text-gray-500">
            Please wait while we establish a secure connection with your Google Account...
          </p>
        </div>
        <div className="text-xs text-gray-400 bg-gray-50 p-3 rounded-lg">
          Do not close this window or refresh the page.
        </div>
      </div>
    </div>
  );
};

export default OAuth2Callback;
